import React, {Fragment, useEffect, useMemo, useState} from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import {withTranslation} from "react-i18next";
import {connect, useSelector} from "react-redux";
import {useLocation, useNavigate} from "react-router-dom";
import {HashLink} from "react-router-hash-link";
import {compose} from "redux";
import {
  getCustomPageInternalUrl,
  getNodeCategoriesInternalUrl,
  getNodeDashboardsInternalUrl,
  getNodeInternalUrl
} from "../../constants/appRoutes";
import useLanguages from "../../hooks/useLanguages";
import {NodeVisibility} from "../../model/IHubMinimalNode.d.ts";
import {ConfigNodeMenuId} from "../../model/item-containers-models/configNodeMenuId";
import CardsGrid from "../cards-grid";
import Footer from "../footer";
import Header from "../header";
import Hero from "../hero";
import ModulesPlaceholder from "../modules-placeholder";
import Page from "../page";
import PageSection from "../page-section";
import SanitizedHTML from "../sanitized-html";
import CatalogPreview from "./CatalogPreview";
import {nodeCustomPagesSelector} from "../../state/node/nodeSelectors";
import {openSearchDialog} from "../../state/search-dialog/searchDialogActions";
import {WINDOW_SCREEN_WIDTH_MD} from "../../utils/constants";
import {getCustomPagesInMenu} from "../../utils/itemContainers";
import {getPageTitle} from "../../utils/other";

const mapStateToProps = state => ({
  isA11y: state.app.isA11y,
  clientBasePath: state.config.clientBasePath,
  baseURL: state.config.baseURL,
  hub: state.hub,
  node: state.node,
  catalog: state.catalog,
  moduleConfigs: state.moduleConfigs,
  themeConfig: state.themeConfig
});

const mapDispatchToProps = dispatch => ({
  onSearch: () => dispatch(openSearchDialog())
});

const sectionStyle = {
  scrollMargin: "80px"
};

const scrollToElement = elementId => {
  const element = document.getElementById(elementId);
  if (element) {
    element.scrollIntoView();
  }
};

const Node = ({
  t,
  isA11y,
  clientBasePath,
  baseURL,
  hub,
  nodeCode,
  isDefault,
  node,
  catalog,
  onSearch,
  moduleConfigs,
  themeConfig
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const {localizeI18nObj} = useLanguages();

  const [isSearchVisible, setIsSearchVisible] = useState(false);

  useEffect(() => {
    const setSearchButtonVisibility = () => {
      setIsSearchVisible(window.innerWidth > WINDOW_SCREEN_WIDTH_MD);
    };
    setSearchButtonVisibility();
    window.addEventListener("resize", setSearchButtonVisibility);
    return () => window.removeEventListener("resize", setSearchButtonVisibility);
  }, []);

  const nodeMinimalInfo = hub.nodes.find(({code}) => code.toLowerCase() === nodeCode.toLowerCase());

  const otherNodes = hub.nodes.filter(
    n => n.code.toLowerCase() !== nodeCode.toLowerCase() && n.visible !== NodeVisibility.No
  );

  const params = new URLSearchParams(location.search);
  params.set("accessible", "true");
  const paramsStr = params.toString();
  const path = location.pathname;

  const nodeCustomPages = useSelector(nodeCustomPagesSelector);
  const customPagesInCentralMenu = getCustomPagesInMenu(nodeCustomPages, ConfigNodeMenuId.CENTRAL);

  const hasDashboards = useMemo(() => {
    if (isA11y) {
      return false;
    } else {
      return (
        (
          node?.itemContainers?.dashboard?.uncategorizedDashboards?.concat(
            node?.itemContainers?.dashboard?.categorizedDashboards
          ) ?? []
        ).length > 0
      );
    }
  }, [isA11y, node]);

  return (
    <Fragment>
      {!isA11y && (
        <a href={path + (paramsStr.length > 0 ? "?" : "") + paramsStr} target="_self" className="skip-link sr-only">
          {t("commons.hashLinks.accessible")}
        </a>
      )}
      <HashLink to={{hash: "#main", search: location.search}} className="skip-link sr-only">
        {t("commons.hashLinks.main")}
      </HashLink>
      {node && node.description && node.description.length > 0 && (
        <HashLink to={{hash: "#node-information", search: location.search}} className="skip-link sr-only">
          {t("commons.hashLinks.nodeInformation")}
        </HashLink>
      )}
      {isDefault && otherNodes.length > 0 && (
        <HashLink to={{hash: "#other-nodes", search: location.search}} className="skip-link sr-only">
          {t("commons.hashLinks.otherNodes")}
        </HashLink>
      )}
      <HashLink to={{hash: "#footer", search: location.search}} className="skip-link sr-only">
        {t("commons.hashLinks.footer")}
      </HashLink>
      <Page
        id={isDefault ? "landing-page" : "node-page"}
        title={getPageTitle([nodeMinimalInfo?.name, hub?.hub?.name], t)}
      >
        <Header isDefault={isDefault} />
        <Box
          component="main"
          sx={{
            width: "100%",
            height: "100%"
          }}
          id="main"
          className={`node`}
        >
          <Hero
            title={nodeMinimalInfo.name}
            slogan={node && node.slogan}
            logo={node && nodeMinimalInfo.logoURL && <img src={baseURL + nodeMinimalInfo.logoURL} alt="" />}
            background={nodeMinimalInfo?.backgroundMediaURL}
            backgroundFallback={
              <div
                style={{
                  background: `url("${clientBasePath}/config/images/default-node-background.jpg") center center / cover no-repeat`
                }}
              />
            }
            showExtraLogos={isDefault}
          >
            <Grid container justifyContent="center" spacing={2}>
              {node && node.description && node.description.length > 0 && (
                <Grid id="infos-btn">
                  <Button
                    id="node_home_infos_btn"
                    size="large"
                    color="secondary"
                    variant="contained"
                    onClick={() => scrollToElement("node-information")}
                  >
                    {t("scenes.node.informations")}
                  </Button>
                </Grid>
              )}
              <ModulesPlaceholder
                id="node-home-buttons"
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  justifyContent: "center",
                  alignContent: "center",
                  marginLeft: "-8px",
                  marginRight: "-8px"
                }}
              />
              {catalog && !catalog.isEmpty && (
                <Grid id="explore-btn">
                  <Button
                    id="node_home_categories_btn"
                    size="large"
                    color="secondary"
                    variant="contained"
                    onClick={() => navigate(getNodeCategoriesInternalUrl(nodeCode))}
                  >
                    {t("scenes.node.explore")}
                  </Button>
                </Grid>
              )}
              {catalog && !catalog.isEmpty ? (
                <Grid id="search-btn">
                  <ModulesPlaceholder
                    id="node-home-buttons__search"
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      justifyContent: "center",
                      alignContent: "center"
                    }}
                    DefaultComponent={
                      isSearchVisible && (
                        <Button
                          id="node_home_search_btn"
                          size="large"
                          color="secondary"
                          variant="contained"
                          onClick={onSearch}
                        >
                          {t("scenes.node.search")}
                        </Button>
                      )
                    }
                  />
                </Grid>
              ) : null}
              {hasDashboards && (
                <Grid id="dashboards-btn">
                  <Button
                    id="node_home_dashboards_btn"
                    size="large"
                    color="secondary"
                    variant="contained"
                    onClick={() => navigate(getNodeDashboardsInternalUrl(node.code))}
                  >
                    {t("scenes.node.dashboards")}
                  </Button>
                </Grid>
              )}
              {isDefault && otherNodes.length > 0 && (
                <Grid id="other-nodes-btn">
                  <Button
                    id="node_home_other_nodes_btn"
                    size="large"
                    color="secondary"
                    variant="contained"
                    onClick={() => scrollToElement("other-nodes")}
                  >
                    {t("scenes.node.otherNodes")}
                  </Button>
                </Grid>
              )}
              {!isA11y &&
                moduleConfigs.modules.includes("custom-page") &&
                customPagesInCentralMenu.map(v => (
                  <Grid key={v.id}>
                    <Button
                      id="customPages-btn"
                      size="large"
                      color="secondary"
                      variant="contained"
                      onClick={() => navigate(getCustomPageInternalUrl(v.id, node?.code))}
                    >
                      {localizeI18nObj(v.title)}
                    </Button>
                  </Grid>
                ))}
            </Grid>
          </Hero>
          <Box
            sx={{
              backgroundColor: "#f5f5f5",
              width: "100%"
            }}
          >
            <Container sx={{paddingTop: theme => theme.spacing(3)}} id="section-container">
              {node && node.description && node.description.length > 0 && (
                <Box id="description-section">
                  <PageSection
                    id="node-information"
                    className={"node-information"}
                    sx={sectionStyle}
                    sectiontitle={t("scenes.node.informations")}
                  >
                    <Box id="node-information__content" textAlign="justify">
                      <SanitizedHTML html={node.description} allowTarget />
                    </Box>
                  </PageSection>
                </Box>
              )}
              <Box id="catalog-preview-section">
                <PageSection
                  id="catalog-preview"
                  className={"catalog-preview"}
                  sx={sectionStyle}
                  sectiontitle={t("scenes.node.catalogPreview")}
                >
                  <ModulesPlaceholder
                    id="node-home-catalog-section"
                    DefaultComponent={<CatalogPreview node={node} catalog={catalog} baseURL={baseURL} />}
                  />
                </PageSection>
              </Box>
              <ModulesPlaceholder id="node-home-sections" />
              {isDefault && otherNodes.length > 0 && (
                <Box id="other-nodes-section">
                  <PageSection
                    id="other-nodes"
                    className="other-nodes"
                    sx={sectionStyle}
                    sectiontitle={t("scenes.node.otherNodes")}
                  >
                    <Box id="other-nodes__content" sx={{marginTop: "16px"}}>
                      <nav aria-label={t("scenes.node.otherNodes")}>
                        <CardsGrid
                          list={otherNodes
                            .sort((a, b) => a.order - b.order)
                            .map(({code, name, backgroundMediaURL}) => ({
                              code,
                              id: code,
                              label: name,
                              image: backgroundMediaURL
                                ? baseURL + backgroundMediaURL
                                : "/config/images/default-node-background.jpg"
                            }))}
                          getHref={node => getNodeInternalUrl(node?.code)}
                          size={themeConfig.nodesGridProps}
                        />
                      </nav>
                    </Box>
                  </PageSection>
                </Box>
              )}
            </Container>
            <Footer />
          </Box>
        </Box>
      </Page>
    </Fragment>
  );
};

export default compose(withTranslation(), connect(mapStateToProps, mapDispatchToProps))(Node);
